import { formatDatum, EINSATZARTEN, CHAINGANG_STATUS_VALUES, CHAINGANG_STATUS_MAP, todayISO } from "@/lib/constants";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle } from "lucide-react";

const dateCell = (v) => <span className="whitespace-nowrap">{formatDatum(v)}</span>;
const longText = (v) => (
  <span className="block max-w-md whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-zinc-300">{v || "—"}</span>
);

export const WICHTIGKEIT_OPTIONS = ["Stufe 1", "Stufe 2", "Stufe 3", "Stufe 4"];
const stufeBadge = (v) => {
  const map = {
    "Stufe 2": "text-yellow-400 border-yellow-500/40 bg-yellow-500/10",
    "Stufe 3": "text-orange-400 border-orange-500/40 bg-orange-500/10",
    "Stufe 4": "text-red-400 border-red-500/40 bg-red-500/10",
  };
  const cls = map[v] || "text-zinc-400 border-zinc-700 bg-zinc-800/40";
  return <Badge className={`rounded-sm border font-mono text-[10px] whitespace-nowrap ${cls}`}>{v || "Stufe 1"}</Badge>;
};
const WICHTIGKEIT_ROW = {
  "Stufe 2": "bg-yellow-500/10 hover:bg-yellow-500/20",
  "Stufe 3": "bg-orange-500/10 hover:bg-orange-500/20",
  "Stufe 4": "bg-red-500/10 hover:bg-red-500/20",
};
export const wichtigkeitRowClass = (row) => WICHTIGKEIT_ROW[row?.wichtigkeit] || "";
const wichtigkeitField = { name: "wichtigkeit", label: "Wichtigkeit", type: "select", options: WICHTIGKEIT_OPTIONS, required: true };
const stufeColumn = { key: "wichtigkeit", label: "Stufe", render: stufeBadge };

// Informationen -----------------------------------------------------------
export const infoColumns = [
  stufeColumn,
  { key: "datum", label: "Datum", mono: true, render: dateCell },
  { key: "officer", label: "Officer", mono: true },
  { key: "infotext", label: "Infotext", render: longText },
];

export const infoFields = [
  wichtigkeitField,
  { name: "datum", label: "Datum", type: "date", required: true },
  { name: "officer", label: "Officer", type: "text", required: true, placeholder: "Name / Dienstnummer" },
  { name: "infotext", label: "Infotext", type: "textarea", required: true, placeholder: "Information eingeben…" },
];

export const infoDefault = (officer, kategorie) => () => ({
  datum: todayISO(),
  officer: officer || "",
  infotext: "",
  wichtigkeit: "Stufe 1",
  kategorie,
});

// Einzelhaft / U-Haft (mit Name + Zelle vor dem Infotext) -----------------
export const uhaftColumns = [
  stufeColumn,
  { key: "datum", label: "Datum", mono: true, render: dateCell },
  { key: "officer", label: "Officer", mono: true },
  { key: "name", label: "Name", render: (v) => <span className="block max-w-[160px] whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-zinc-200 font-medium">{v || "—"}</span> },
  { key: "zelle", label: "Zelle", mono: true, render: (v) => <span className="block max-w-[70px] truncate">{v || "—"}</span> },
  { key: "infotext", label: "Infotext", render: longText },
];

export const uhaftFields = [
  wichtigkeitField,
  { name: "datum", label: "Datum", type: "date", required: true },
  { name: "officer", label: "Officer", type: "text", required: true, placeholder: "Name / Dienstnummer" },
  { name: "name", label: "Name", type: "textarea", placeholder: "Name des Häftlings" },
  { name: "zelle", label: "Zelle", type: "text", placeholder: "z. B. A-3" },
  { name: "infotext", label: "Infotext", type: "textarea", required: true, placeholder: "Information eingeben…" },
];

export const uhaftDefault = (officer, kategorie) => () => ({
  datum: todayISO(),
  officer: officer || "",
  name: "",
  zelle: "",
  infotext: "",
  wichtigkeit: "Stufe 1",
  kategorie,
});

// Langzeit-Infos (Info-Notizen über den Häftlingskarteien) ----------------
export const lzInfoColumns = [
  { key: "datum", label: "Datum", mono: true, render: dateCell },
  { key: "name", label: "Name des Häftlings", render: (v) => <span className="block max-w-[200px] truncate text-zinc-200 font-medium">{v || "—"}</span> },
  { key: "text", label: "Info", render: longText },
];

export const lzInfoFields = [
  { name: "datum", label: "Datum", type: "date", required: true },
  { name: "name", label: "Name des Häftlings", type: "text", placeholder: "Name des Häftlings" },
  { name: "text", label: "Info", type: "textarea", required: true, placeholder: "Information eingeben…" },
];

export const lzInfoDefault = () => () => ({
  datum: todayISO(),
  name: "",
  text: "",
});

// Telefon Zellen (Kategorie in Allgemeine Informationen) ------------------
export const telefonColumns = [
  stufeColumn,
  { key: "standort", label: "Standort", render: (v) => <span className="block text-zinc-200 font-medium">{v || "—"}</span> },
  { key: "telefonnummer", label: "Telefonnummer", mono: true },
];

export const telefonFields = [
  wichtigkeitField,
  { name: "standort", label: "Standort", type: "text", required: true, placeholder: "z. B. Zellenblock A" },
  { name: "telefonnummer", label: "Telefonnummer", type: "text", required: true, placeholder: "z. B. 555-1234" },
];

export const telefonDefault = (kategorie) => () => ({
  standort: "",
  telefonnummer: "",
  wichtigkeit: "Stufe 1",
  kategorie,
});

// Appellthemen ------------------------------------------------------------
export const appellColumns = [
  { key: "datum", label: "Datum", mono: true, render: dateCell },
  { key: "thema", label: "Thema", render: (v) => <span className="block max-w-[160px] truncate text-zinc-200 font-medium">{v || "—"}</span> },
  { key: "inhalt", label: "Inhalt", render: (v) => <span className="block w-full max-w-md whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-zinc-300">{v || "—"}</span> },
];

export const appellFields = [
  { name: "datum", label: "Datum", type: "date", required: true },
  { name: "thema", label: "Thema", type: "text", required: true, placeholder: "Thema des Appells…" },
  { name: "inhalt", label: "Inhalt", type: "textarea", required: true, placeholder: "Inhalt des Appellthemas…" },
];

export const appellDefault = () => () => ({
  datum: todayISO(),
  thema: "",
  inhalt: "",
});

// Inhaftierung ------------------------------------------------------------
export const inhaftColumns = [
  { key: "datum", label: "Datum", mono: true, render: dateCell },
  { key: "taetername", label: "Tätername" },
  { key: "bemerkung", label: "Bemerkung", render: longText },
  { key: "dienstnummern", label: "Dienstnummern", mono: true },
];

export const inhaftFields = [
  { name: "datum", label: "Datum", type: "date", required: true },
  { name: "taetername", label: "Tätername", type: "text", required: true },
  { name: "bemerkung", label: "Bemerkung", type: "textarea" },
  { name: "dienstnummern", label: "Dienstnummern (wer hat inhaftiert)", type: "text", required: true, placeholder: "z.B. 0123, 0456" },
];

export const inhaftDefault = (officer) => () => ({
  datum: todayISO(),
  taetername: "",
  bemerkung: "",
  dienstnummern: officer || "",
});

// Einsätze ----------------------------------------------------------------
export const einsatzColumns = [
  { key: "datum", label: "Datum", mono: true, render: dateCell },
  {
    key: "einsatzart",
    label: "Einsatzart",
    render: (v) =>
      v ? (
        <Badge className="rounded-sm bg-primary/15 text-primary border border-primary/30 font-mono text-[11px] font-medium">
          {v}
        </Badge>
      ) : (
        "—"
      ),
  },
  { key: "besuchter", label: "Besucht / Täter" },
  { key: "besucher", label: "Besucher / SADCR" },
  { key: "einsatzzeit", label: "Einsatzzeit", mono: true },
  { key: "akte", label: "Akte", mono: true },
  { key: "bemerkung", label: "Bemerkung", render: longText },
];

export const einsatzFields = [
  { name: "datum", label: "Datum", type: "date", required: true },
  { name: "einsatzart", label: "Einsatzart", type: "select", options: EINSATZARTEN, required: true },
  { name: "besuchter", label: "Wer wird besucht? / Täter", type: "text" },
  { name: "besucher", label: "Wer besucht // Wer vom SADCR war beteiligt", type: "text" },
  { name: "einsatzzeit", label: "Einsatzzeit", type: "text", placeholder: "z.B. 14:30 - 15:00" },
  { name: "akte", label: "Akte", type: "text", placeholder: "Aktenzeichen" },
  { name: "bemerkung", label: "Bemerkung", type: "textarea" },
];

export const einsatzDefault = () => () => ({
  datum: todayISO(),
  einsatzart: "",
  besuchter: "",
  besucher: "",
  einsatzzeit: "",
  akte: "",
  bemerkung: "",
});

// Chain-Gangs -------------------------------------------------------------
const statusCell = (v) => {
  if (!v) return "—";
  const positive = CHAINGANG_STATUS_MAP[v];
  const Icon = positive ? CheckCircle2 : XCircle;
  const cls = positive
    ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
    : "text-red-400 border-red-500/30 bg-red-500/10";
  return (
    <Badge className={`rounded-sm border font-mono text-[11px] font-medium gap-1.5 whitespace-nowrap ${cls}`}>
      <Icon className="h-3.5 w-3.5" />
      {v}
    </Badge>
  );
};

export const chainColumns = [
  { key: "datum", label: "Datum", mono: true, render: dateCell },
  { key: "uhrzeit", label: "Uhrzeit", mono: true },
  { key: "name_haeftling", label: "Häftling" },
  { key: "status", label: "Anfrage – Antwort", render: statusCell },
  { key: "ort_taetigkeit", label: "Ort / Tätigkeit" },
  { key: "was_gemacht", label: "Was wurde gemacht?", render: longText },
  { key: "wieso_abgelehnt", label: "Abgelehnt? / Infos", render: longText },
  { key: "saecke", label: "Säcke", mono: true },
  { key: "verkuerzte_zeit", label: "Verk. Zeit (Min.)", mono: true },
  { key: "leitender_officer", label: "Leit. Officer", mono: true },
  { key: "anwesende_kollegen", label: "Anwesende Kollegen" },
];

export const chainFields = [
  { name: "datum", label: "Datum", type: "date", required: true },
  { name: "uhrzeit", label: "Uhrzeit", type: "time" },
  { name: "name_haeftling", label: "Name Häftling", type: "text", required: true },
  { name: "status", label: "Anfrage – Antwort", type: "select", options: CHAINGANG_STATUS_VALUES, required: true },
  { name: "ort_taetigkeit", label: "Ort / Tätigkeit", type: "text" },
  { name: "was_gemacht", label: "Was wurde gemacht?", type: "textarea" },
  { name: "wieso_abgelehnt", label: "Wieso abgelehnt? / Sonstige Infos", type: "textarea" },
  { name: "saecke", label: "Säcke", type: "text", placeholder: "Anzahl", setsField: "verkuerzte_zeit", factor: 45 },
  { name: "verkuerzte_zeit", label: "Verkürzte Zeit in Min. (1 Sack = 45 Min.)", type: "text", placeholder: "z.B. 30" },
  { name: "leitender_officer", label: "Leitender Officer", type: "text" },
  { name: "anwesende_kollegen", label: "Anwesende Kollegen", type: "text", placeholder: "Dienstnummern / Namen" },
];

export const chainDefault = (officer) => () => ({
  datum: todayISO(),
  uhrzeit: "",
  name_haeftling: "",
  status: "",
  ort_taetigkeit: "",
  was_gemacht: "",
  wieso_abgelehnt: "",
  saecke: "",
  verkuerzte_zeit: "",
  leitender_officer: officer || "",
  anwesende_kollegen: "",
});

// Verbrecherregister ------------------------------------------------------
const linkCell = (v) =>
  v && /^https?:\/\//i.test(v) ? (
    <a href={v} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">
      Öffnen ↗
    </a>
  ) : (
    v || <span className="text-zinc-600">—</span>
  );

export const registerColumns = [
  { key: "name", label: "Name" },
  { key: "agv", label: "AGV", mono: true, render: (v, row) => (v ? (row.agv_unklar ? `AGV-Unklar ${v}` : v) : "—") },
  { key: "telefonnummer", label: "Telefon", mono: true },
  { key: "perso_akte", label: "Perso-Akte", render: linkCell },
  { key: "schnatter", label: "Schnatter", render: linkCell },
  { key: "arbeitet_bei", label: "Arbeitet bei" },
];

export const registerFields = [
  { name: "name", label: "Name", type: "text", required: true },
  { name: "agv", label: "AGV", type: "text" },
  { name: "telefonnummer", label: "Telefonnummer", type: "text" },
  { name: "perso_akte", label: "Perso-Akte (Link)", type: "text", placeholder: "https://…" },
  { name: "schnatter", label: "Schnatter (Link)", type: "text", placeholder: "https://…" },
  { name: "arbeitet_bei", label: "Arbeitet bei", type: "text" },
];

export const registerDefault = () => () => ({
  name: "",
  agv: "",
  agv_unklar: false,
  telefonnummer: "",
  perso_akte: "",
  schnatter: "",
  arbeitet_bei: "",
});

// Externe Dokumente -------------------------------------------------------
export const externeDokColumns = [
  {
    key: "label",
    label: "Label",
    render: (v, row) =>
      row.link && /^https?:\/\//i.test(row.link) ? (
        <a href={row.link} target="_blank" rel="noreferrer" className="text-primary hover:underline font-medium inline-flex items-center gap-1.5">
          {v || "Dokument"} <span className="text-xs">↗</span>
        </a>
      ) : (
        <span className="text-zinc-200 font-medium">{v || "—"}</span>
      ),
  },
];

export const externeDokFields = [
  { name: "label", label: "Label / Bezeichnung", type: "text", required: true },
  { name: "link", label: "Verlinkung", type: "text", required: true, placeholder: "https://…" },
];

export const externeDokDefault = (kategorie) => () => ({ label: "", link: "", kategorie });

// Fehlerliste -------------------------------------------------------------
export const fehlerColumns = [
  {
    key: "aktenname",
    label: "Aktenname",
    render: (v, row) =>
      row.akten_link && /^https?:\/\//i.test(row.akten_link) ? (
        <a href={row.akten_link} target="_blank" rel="noreferrer" className="text-primary hover:underline font-medium inline-flex items-center gap-1.5">
          {v || "Akte"} <span className="text-xs">↗</span>
        </a>
      ) : (
        <span className="text-zinc-200 font-medium">{v || "—"}</span>
      ),
  },
  { key: "officer", label: "Officer", mono: true },
  { key: "department", label: "Department" },
  { key: "bemerkung", label: "Bemerkung", render: longText },
];

export const fehlerFields = [
  { name: "aktenname", label: "Aktenname", type: "text", required: true },
  { name: "akten_link", label: "Verlinkung (Link)", type: "text", placeholder: "https://…" },
  { name: "officer", label: "Officer", type: "text" },
  { name: "department", label: "Department", type: "text" },
  { name: "bemerkung", label: "Bemerkung", type: "textarea" },
];
